import { useState } from 'react';
import { NavLink, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  ClipboardList, Store, MapPinned, Tag, UtensilsCrossed,
  BarChart3, Menu, X, LogOut, ChevronLeft, Home, Percent, Ticket, Megaphone, Users
} from 'lucide-react';

const NAV_ITEMS = [
  { to: '/admin/pedidos', icon: ClipboardList, label: 'Pedidos', roles: ['admin_global', 'recepcion_sucursal', 'manager_sucursal', 'operador_pedidos'] },
  { to: '/admin/sucursales', icon: Store, label: 'Sucursales', roles: ['admin_global', 'recepcion_sucursal'] },
  { to: '/admin/zonas', icon: MapPinned, label: 'Zonas', roles: ['admin_global'] },
  { to: '/admin/categorias', icon: Tag, label: 'Categorias', roles: ['admin_global'] },
  { to: '/admin/productos', icon: UtensilsCrossed, label: 'Productos', roles: ['admin_global'] },
  { to: '/admin/promociones', icon: Percent, label: 'Promos', roles: ['admin_global'] },
  { to: '/admin/cupones', icon: Ticket, label: 'Cupones', roles: ['admin_global'] },
  { to: '/admin/usuarios', icon: Users, label: 'Usuarios', roles: ['admin_global'] },
  { to: '/admin/promos-push', icon: Megaphone, label: 'Push Promos', roles: ['admin_global'] },
  { to: '/admin/configuracion-home', icon: Home, label: 'Portada', roles: ['admin_global'] },
  { to: '/admin/reportes', icon: BarChart3, label: 'Reportes', roles: ['admin_global'] },
];

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#E84C1C] border-t-transparent rounded-full animate-spin" /></div>;

  const roles = user?.roles || [];
  const isAdmin = roles.includes('admin_global');
  const hasAdminRole = roles.some(r => ['admin_global', 'recepcion_sucursal', 'manager_sucursal', 'operador_pedidos'].includes(r));
  if (!user || !hasAdminRole) return <Navigate to="/" replace />;

  const visibleNav = NAV_ITEMS.filter(n => n.roles.some(r => roles.includes(r)));
  const branchName = !isAdmin && user.branch_ids?.length > 0 ? `Sucursal #${user.branch_ids[0]}` : null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Sidebar overlay mobile */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-60 bg-zinc-950 border-r border-zinc-800/50 flex flex-col transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-14 flex items-center justify-between px-4 border-b border-zinc-800/50">
          <a href="/admin" className="flex items-center gap-2">
            <img src="/logo-transparent.png" alt="Sushi Express" className="h-10 w-auto drop-shadow-[0_0_10px_rgba(232,76,28,0.4)]" />
            <span className="text-[10px] text-zinc-500 font-medium uppercase tracking-wider">Admin</span>
          </a>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-zinc-400"><X size={18} /></button>
        </div>
        <div className="h-px bg-gradient-to-r from-transparent via-[#E84C1C]/30 to-transparent" />
        {branchName && (
          <div className="px-4 py-2 border-b border-zinc-800/30">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Sucursal</p>
            <p className="text-xs text-[#E84C1C] font-medium truncate">{branchName}</p>
          </div>
        )}
        <nav className="flex-1 py-2 space-y-0.5 px-2 overflow-y-auto">
          {visibleNav.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`admin-nav-${label.toLowerCase()}`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-[#E84C1C]/10 text-[#E84C1C] font-medium' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'}`
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-800/50 space-y-2">
          <NavLink to="/" className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-zinc-500 hover:text-white hover:bg-zinc-800/50 transition-colors">
            <ChevronLeft size={14} /> Volver a la tienda
          </NavLink>
          <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors">
            <LogOut size={14} /> Cerrar sesion
          </button>
          <p className="text-[10px] text-zinc-600 px-3 truncate">{user.email}</p>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 flex items-center gap-3 px-4 border-b border-zinc-800/50 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-zinc-400 hover:text-white" data-testid="admin-menu-toggle">
            <Menu size={20} />
          </button>
          <h1 className="text-sm font-semibold text-zinc-300">{user.nombre}</h1>
          {isAdmin && <span className="text-[10px] bg-[#E84C1C]/15 text-[#E84C1C] px-2 py-0.5 rounded-full font-medium">Admin</span>}
          {roles.includes('recepcion_sucursal') && <span className="text-[10px] bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full font-medium">Recepcion</span>}
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
